import { Calendar } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { cn } from '@/lib/cn';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Label } from '@heidi/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';

import { personalizationSharedMessages } from '../shared/personalization-shared.messages';
import { RequestDemoPhoneAndReasonFields } from '../shared/RequestDemoPhoneAndReasonFields';

interface TimeslotItem {
  value: string;
  label: string;
}

interface WebinarOptInSectionProps {
  ctaOptIn: boolean;
  selectedTimeslotValue?: string;
  timeslotItems: TimeslotItem[];
  isLoadingTimeslots: boolean;
  demoReason: string;
  phoneNumber: string;
  defaultCountry?: Country;
  /** Country name forwarded to the DEV-11979 reason_for_demo_phone field_selected event. */
  countryName?: string;
  /** Language code forwarded to the DEV-11979 reason_for_demo_phone field_selected event. */
  language?: string;
  onCtaOptInChange: (checked: boolean) => void;
  onTimeslotSelect: (value: string | undefined) => void;
  onDemoReasonChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
}

export function WebinarOptInSection({
  ctaOptIn,
  selectedTimeslotValue,
  timeslotItems,
  isLoadingTimeslots,
  demoReason,
  phoneNumber,
  defaultCountry,
  countryName,
  language,
  onCtaOptInChange,
  onTimeslotSelect,
  onDemoReasonChange,
  onPhoneNumberChange,
}: WebinarOptInSectionProps) {
  const { t } = useTranslations();

  return (
    <div className="flex w-full max-w-[400px] flex-col gap-3 rounded-xl border border-border bg-validation-info-muted p-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="demo-webinar"
          checked={ctaOptIn}
          onCheckedChange={(checked) => onCtaOptInChange(checked === true)}
        />
        <Label htmlFor="demo-webinar" className="text-text-primary">
          <FormattedMessage {...personalizationSharedMessages.bookDemoWebinarSignup} />
        </Label>
      </div>

      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
          ctaOptIn ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="flex flex-col gap-3 overflow-hidden">
          <div className="flex w-full flex-col gap-1">
            <TypographyP2Bold id="webinar-timeslot-label" className="leading-tight">
              {t('setupPage.webinarTimeslotLabel')}
            </TypographyP2Bold>
            <Select
              value={selectedTimeslotValue}
              onValueChange={onTimeslotSelect}
              disabled={!ctaOptIn || isLoadingTimeslots}
              aria-labelledby="webinar-timeslot-label"
            >
              <SelectTrigger>
                <Calendar size={16} className="mr-1 text-text-secondary" />
                <SelectValue
                  placeholder={
                    isLoadingTimeslots
                      ? t('setupPage.webinarTimeslotLoading')
                      : t('setupPage.webinarTimeslotPlaceholder')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {timeslotItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <RequestDemoPhoneAndReasonFields
            optional
            phoneNumber={phoneNumber}
            onPhoneNumberChange={onPhoneNumberChange}
            defaultCountry={defaultCountry}
            demoReason={demoReason}
            onDemoReasonChange={onDemoReasonChange}
            disabled={!ctaOptIn}
            country={countryName}
            language={language}
          />
        </div>
      </div>
    </div>
  );
}
